import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandMoreIcon,
  Lightbulb as TemplateIcon,
  PlayCircleOutline as RunIcon,
  ArrowBack as BackIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  RemoveCircleOutline as RemoveIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { apiUrl } from '../../config/api';

import TaskValueAnalysisWorkshop from './TaskValueAnalysisWorkshop';
import GoalAlignmentWorkshop from './GoalAlignmentWorkshop';
import SkillsMentoringWorkshop from './SkillsMentoringWorkshop';

const localWorkshopTemplates = [
  {
    id: 'goal-alignment',
    title: 'Goal Alignment',
    description: 'Identify and map team member goals for better collaboration',
    duration: '2 hours',
    participants: '6-12 people',
    activities: [
      'Individual goal sharing (20 min)',
      'Goal mapping exercise (45 min)',
      'Overlap identification (30 min)',
      'Action planning (25 min)'
    ],
    default_flow: ['goal-alignment'],
  },
  {
    id: 'task-value-analysis',
    title: 'Task Value Analysis',
    description: 'Optimize task distribution using enhanced Eisenhower matrix',
    duration: '90 minutes',
    participants: '5-10 people',
    activities: [
      'Task inventory (15 min)',
      'Eisenhower matrix mapping (30 min)',
      'Growth value assessment (25 min)',
      'Delegation planning (20 min)'
    ],
    default_flow: ['task-value-analysis'],
  },
  {
    id: 'skills-mentoring',
    title: 'Skills Mentoring',
    description: 'Identify and mentor team members on specific skills',
    duration: '60 minutes',
    participants: '2-6 people',
    activities: [
      'Skills inventory (15 min)',
      'Skills mentoring (45 min)'
    ],
    default_flow: ['skills-mentoring'],
  },
  {
    id: 'strategy-alignment',
    title: 'Strategy Alignment',
    description: 'Analyze your tasks in depth, then align your goals for better team strategy.',
    duration: '90 minutes',
    participants: 'Full Team',
    activities: [
      'Task Value Analysis',
      'Goal Alignment'
    ],
    default_flow: ['task-value-analysis', 'goal-alignment'],
  },
];

// --- Workshop Building Blocks Definition ---
const workshopBuildingBlocks = [
  { id: 'task-value-analysis', name: 'Task Value Analysis Workshop', component: TaskValueAnalysisWorkshop },
  { id: 'goal-alignment', name: 'Goal Alignment Workshop', component: GoalAlignmentWorkshop },
  { id: 'skills-mentoring', name: 'Skills Mentoring Workshop', component: SkillsMentoringWorkshop },
];

const workshopRegistry: { [key: string]: React.FC } = workshopBuildingBlocks.reduce((acc, block) => {
  acc[block.id] = block.component;
  return acc;
}, {} as { [key: string]: React.FC });

// --- Interfaces ---
interface Workshop {
  id: number;
  title: string;
  description: string;
  date: string;
  facilitator: string;
  participants: string;
  participants_list?: TeamMember[];
  status: string;
  notes: string;
  module_flow?: string[]; // Made optional to handle old data
  created_at: string;
  updated_at: string;
}

interface WorkshopFormData {
  title: string;
  description: string;
  date: string;
  facilitator: string;
  participants: string;
  selectedParticipants: number[];
  status: string;
  notes: string;
  module_flow: string[];
}

interface TeamMember {
  id: number;
  user_name: string;
  user_email: string;
  role: string;
  skills?: string;
}

interface WorkshopTemplate {
  id: string;
  title: string;
  description: string;
  duration: string;
  participants: string;
  activities: string[];
  default_flow: string[]; // <-- Important for "Run Now"
}

type WorkshopAgendaSource = {
  title: string;
  flow: string[]; // actual step/module IDs
};

// --- Main Workshops Component ---
const Workshops: React.FC = () => {
  const { user, currentTeam } = useAuth();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [templates] = useState(localWorkshopTemplates);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const [runningWorkshop, setRunningWorkshop] = useState<WorkshopAgendaSource | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const [formData, setFormData] = useState<WorkshopFormData>({
    title: '',
    description: '',
    date: '',
    facilitator: user?.displayName || '',
    participants: '',
    selectedParticipants: [],
    status: 'planned',
    notes: '',
    module_flow: [],
  });

  const isAdmin = currentTeam?.role === 'admin' || currentTeam?.role === 'manager';


  const fetchWorkshops = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(apiUrl('/api/workshops'));
      setWorkshops(response.data);
    } catch (err) {
      setError('Failed to load workshops');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies since we don't need to re-fetch when team changes

  const fetchTeamMembers = React.useCallback(async () => {
    try {
      if (!currentTeam) return;
      const response = await axios.get(apiUrl(`/api/team-members/team/${currentTeam.id}`));
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  }, [currentTeam]);


  // --- Data Fetching ---
  useEffect(() => {
    fetchWorkshops();
    if (currentTeam && isAdmin) {
      fetchTeamMembers();
    }
  }, [currentTeam, isAdmin, fetchWorkshops, fetchTeamMembers]);


  function formatForDatetimeLocal(dateStr: string) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const tzOff = date.getTimezoneOffset() * 60000;
    const localISO = new Date(date.getTime() - tzOff).toISOString().slice(0,16);
    return localISO;
  }

 
  // --- Form & Dialog Handlers ---
  const handleOpenDialog = (workshop: Workshop | null = null, template: WorkshopTemplate | null = null) => {
    setEditingWorkshop(workshop);
    if (workshop) {
      setFormData({
        title: workshop.title,
        description: workshop.description,
        date: workshop.date.split('T')[0],
        facilitator: workshop.facilitator,
        participants: workshop.participants,
        selectedParticipants: workshop.participants_list?.map(p => p.id) || [],
        status: workshop.status,
        notes: workshop.notes,
        module_flow: workshop.module_flow || [],
      });
    } else {
      setFormData({
        title: template?.title || '',
        description: template?.description || '',
        date: '',
        facilitator: user?.displayName || '',
        participants: template?.participants || '',
        selectedParticipants: [],
        status: 'planned',
        notes: template ? `Duration: ${template.duration}\n\nActivities:\n${template.activities.map(a => `• ${a}`).join('\n')}` : '',
        module_flow: template?.default_flow || [],
      });
    }
    setDialogOpen(true);
  };

  const handleFormChange = (field: keyof WorkshopFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        selectedParticipants: formData.selectedParticipants || [],
        module_flow: formData.module_flow,
      };
      console.log(submitData);
      if (editingWorkshop) {
        await axios.put(apiUrl(`/api/workshops/${editingWorkshop.id}`), submitData);
      } else {
        await axios.post(apiUrl('/api/workshops'), submitData);
      }
      setDialogOpen(false);
      fetchWorkshops();
    } catch (err) {
      setError('Failed to save workshop. Note: The backend may not support saving custom agendas yet.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure?')) {
      try {
        await axios.delete(apiUrl(`/api/workshops/${id}`));
        fetchWorkshops();
      } catch (err) {
        setError('Failed to delete workshop');
      }
    }
  };
  
  // --- Workshop Builder Handlers ---
  const handleAddActivity = (id: string) => {
    if (!formData.module_flow.includes(id)) {
      handleFormChange('module_flow', [...formData.module_flow, id]);
    }
  };
  const handleRemoveActivity = (index: number) => {
    const newFlow = [...formData.module_flow];
    newFlow.splice(index, 1);
    handleFormChange('module_flow', newFlow);
  };
  const handleMoveActivity = (index: number, direction: 'up' | 'down') => {
    const newFlow = [...formData.module_flow];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFlow.length) return;
    [newFlow[index], newFlow[targetIndex]] = [newFlow[targetIndex], newFlow[index]];
    handleFormChange('module_flow', newFlow);
  };
  
  // --- Session Player Handlers ---
  const handleRunNow = (agenda: WorkshopAgendaSource) => {
    setRunningWorkshop(agenda);
    setCurrentStep(0);
  };

  // --- RENDER LOGIC ---
  if (loading) return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Container>;

  // ** View 1: Workshop Session Player **
  if (runningWorkshop) {
    if (!runningWorkshop.flow || runningWorkshop.flow.length === 0) {
      return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Button startIcon={<BackIcon />} onClick={() => setRunningWorkshop(null)} sx={{ mb: 2 }}>Back to Workshops</Button>
          <Alert severity="error">This workshop has no activities defined and cannot be run.</Alert>
        </Container>
      );
    }
    const activityId = runningWorkshop.flow[currentStep];
    const ActiveWorkshopComponent = workshopRegistry[activityId];
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} p={2} component={Card}>
           <Button startIcon={<BackIcon />} onClick={() => setRunningWorkshop(null)}>End Session</Button>
           <Typography variant="h6">{runningWorkshop.title} (Step {currentStep + 1}/{runningWorkshop.flow.length})</Typography>
           <Box>
             <Button startIcon={<PrevIcon />} disabled={currentStep === 0} onClick={() => setCurrentStep(s => s - 1)}>Prev</Button>
             <Button endIcon={<NextIcon />} disabled={currentStep >= runningWorkshop.flow.length - 1} onClick={() => setCurrentStep(s => s + 1)}>Next</Button>
           </Box>
        </Box>
        {ActiveWorkshopComponent ? <ActiveWorkshopComponent /> : <Alert severity="warning">This workshop step could not be loaded.</Alert>}
      </Container>
    );
  }

  // ** View 2: Default Page with Lists and Templates **
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>Workshop Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Plan Custom Workshop</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}><Tab label="My Workshops" /><Tab label="Templates" /></Tabs>
      </Box>

      {tabValue === 0 && (
         <>
          {workshops.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center' }}><Typography>No workshops planned yet. Create one or use a template.</Typography></Card>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {workshops.map((ws) => (
                  <Box key={ws.id} sx={{ flex: '1 1 400px' }}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6">{ws.title}</Typography>
                        <Chip icon={<ScheduleIcon />} label={new Date(ws.date).toLocaleString()} size="small" sx={{ my: 1 }} />
                      </CardContent>
                      <CardActions>
                        <Typography variant="caption" sx={{mr: 'auto'}}>
                          {ws.module_flow?.length || 0} activities
                        </Typography>
                        <Button
                          startIcon={<RunIcon />}
                          size="small"
                          onClick={() => handleRunNow({ title: ws.title, flow: ws.module_flow || [] })}
                          disabled={!ws.module_flow || ws.module_flow.length === 0 || ws.date > new Date().toISOString()}
                        >
                          Run
                        </Button>
                        <IconButton size="small" onClick={() => handleOpenDialog(ws)}><EditIcon /></IconButton>
                        <IconButton size="small" onClick={() => handleDelete(ws.id)}><DeleteIcon /></IconButton>
                      </CardActions>
                    </Card>
                  </Box>
                )
              )}
            </Box>
          )}
        </>
      )}

      {tabValue === 1 && (
        <Box>
          {templates.map((template) => (
            <Accordion key={template.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <TemplateIcon color="primary" sx={{ mr: 2 }} />
                <Typography variant="h6">{template.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                  <Typography paragraph>{template.description}</Typography>
                  <Box sx={{ display: 'flex', gap: 2}}>
                    <Button variant="outlined" onClick={() => handleOpenDialog(null, template)} startIcon={<AddIcon />}>Plan Workshop</Button>
                    <Button variant="contained" color="secondary" startIcon={<RunIcon />} onClick={() => handleRunNow({ title: template.title, flow: template.default_flow || [] })} disabled={!template.default_flow || template.default_flow.length === 0}>Run Now</Button>
                  </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingWorkshop ? 'Edit Workshop' : 'Plan New Workshop'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField label="Title" value={formData.title} onChange={(e) => handleFormChange('title', e.target.value)} required />
            <TextField label="Description" value={formData.description} onChange={(e) => handleFormChange('description', e.target.value)} multiline rows={2} />
            <TextField
              label="Date & Time"
              type="datetime-local"
              value={formData.date ? formatForDatetimeLocal(formData.date) : ''}
              onChange={e => handleFormChange('date', e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
            />


            <FormControl fullWidth>
              <InputLabel id="participants-select-label">Select Participants</InputLabel>
              <Select
                labelId="participants-select-label"
                multiple
                value={formData.selectedParticipants}
                onChange={(e) => {
                  // ALWAYS an array of numbers!
                  const value = Array.isArray(e.target.value) ? e.target.value.map(Number) : [];
                  setFormData(prev => ({ ...prev, selectedParticipants: value }));
                }}
                // ...other props...
              >
                {teamMembers.map(member => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.user_name || member.user_email} ({member.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
                        
            <Divider sx={{ my: 1 }}><Chip label="Workshop Agenda Builder" /></Divider>
            
            <FormControl fullWidth>
              <InputLabel>Add an Activity</InputLabel>
              <Select value="" onChange={(e) => handleAddActivity(e.target.value as string)} label="Add an Activity">
                {workshopBuildingBlocks.map(block => (
                  <MenuItem key={block.id} value={block.id} disabled={formData.module_flow.includes(block.id)}>{block.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.module_flow.length > 0 &&
              <List sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                {formData.module_flow.map((activityId, index) => {
                  const block = workshopBuildingBlocks.find(b => b.id === activityId);
                  return (
                    <ListItem key={index} secondaryAction={
                      <>
                        <IconButton size="small" onClick={() => handleMoveActivity(index, 'up')} disabled={index === 0}><UpIcon /></IconButton>
                        <IconButton size="small" onClick={() => handleMoveActivity(index, 'down')} disabled={index === formData.module_flow.length - 1}><DownIcon /></IconButton>
                        <IconButton size="small" onClick={() => handleRemoveActivity(index)}><RemoveIcon /></IconButton>
                      </>
                    }>
                      <ListItemText primary={`${index + 1}. ${block?.name || 'Unknown Activity'}`} />
                    </ListItem>
                  )
                })}
              </List>
            }
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editingWorkshop ? 'Save Changes' : 'Plan Workshop'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Workshops;